import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Lock, RefreshCw, Smartphone, User, UserPlus, Router as RouterIcon, WifiOff } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api, BASE, originMismatch, pingServer } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [net, setNet] = useState(null); // hasil ping server
  const [checking, setChecking] = useState(false);
  const [netError, setNetError] = useState(false);
  const [brand, setBrand] = useState({ logo: "", app_name: "ALPHA BILL" });
  const mismatch = originMismatch();

  useEffect(() => {
    api.get("/public/landing")
      .then(({ data }) => setBrand({ logo: data.logo || "", app_name: data.app_name || "ALPHA BILL" }))
      .catch((e) => console.warn("login: gagal memuat logo", e?.message || e));
  }, []);

  const check = async () => {
    setChecking(true);
    setNet(await pingServer());
    setChecking(false);
  };

  useEffect(() => { check(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const res = await login(username, password);
    setBusy(false);
    if (!res.ok) {
      setNetError(!!res.network);
      if (res.network) check();
      return toast.error(res.error);
    }
    setNetError(false);
    toast.success(`Selamat datang, ${res.user.name || res.user.username}`);
    nav(["mitra", "user"].includes(res.user.role) ? "/portal" : "/dashboard", { replace: true });
  };

  const serverDown = netError || (net && !net.ok);
  const appUrl = (() => { try { return new URL(BASE).origin; } catch { return BASE; } })();

  return (
    <div className="relative grain min-h-screen bg-background lg:grid lg:grid-cols-2">
      <div className="relative hidden overflow-hidden lg:block">
        <img
          src="https://images.unsplash.com/photo-1675334758735-5f989ff8237f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODd8MHwxfHNlYXJjaHwxfHxhYnN0cmFjdCUyMGJsdWUlMjBwdXJwbGUlMjBuZW9uJTIwdGVjaCUyMGJhY2tncm91bmR8ZW58MHx8fHwxNzg3Mjc1NzQ3fDA&ixlib=rb-4.1.0&q=85"
          alt="Latar abstrak biru ungu"
          className="absolute inset-0 h-full w-full object-cover opacity-70"
        />
        <div className="absolute inset-0 bg-gradient-to-tr from-background via-background/60 to-transparent" />
        <div className="relative z-10 flex h-full flex-col justify-between p-14">
          <div className="flex items-center gap-3">
            {brand.logo ? (
              <img src={brand.logo} alt={brand.app_name} className="h-10 w-10 rounded-xl object-contain" data-testid="login-logo" />
            ) : (
              <div className="grid h-10 w-10 place-items-center rounded-xl bg-gradient-to-br from-cyan-400 to-violet-600">
                <RouterIcon className="h-5 w-5 text-white" />
              </div>
            )}
            <span className="font-display text-lg font-semibold tracking-tight">{brand.app_name}</span>
          </div>
          <div className="max-w-md">
            <p className="text-[11px] uppercase tracking-[0.28em] text-muted-foreground">Billing RT/RW Net</p>
            <h1 className="mt-4 font-display text-4xl font-semibold leading-tight tracking-tight lg:text-5xl">
              Kendalikan <span className="text-grad">Mikrotik</span> Anda dari satu dasbor.
            </h1>
            <p className="mt-6 text-sm leading-relaxed text-muted-foreground">
              Hotspot, PPPoE/FTTH, voucher, saldo mitra, dan monitoring trafik real-time — terintegrasi RouterOS REST API.
            </p>
          </div>
          <div className="font-mono text-[11px] text-muted-foreground">RouterOS v7 · REST API · WebSocket live</div>
        </div>
      </div>

      <div className="relative z-10 flex min-h-screen items-center justify-center px-6 py-16">
        <form onSubmit={submit} className="fade-up w-full max-w-sm space-y-7" data-testid="login-form">
          <div className="lg:hidden flex items-center gap-3">
            {brand.logo ? (
              <img src={brand.logo} alt={brand.app_name} className="h-10 w-10 rounded-xl object-contain" data-testid="login-logo" />
            ) : (
              <div className="grid h-10 w-10 place-items-center rounded-xl bg-gradient-to-br from-cyan-400 to-violet-600">
                <RouterIcon className="h-5 w-5 text-white" />
              </div>
            )}
            <span className="font-display text-lg font-semibold">{brand.app_name}</span>
          </div>
          <div>
            <p className="text-[11px] uppercase tracking-[0.28em] text-muted-foreground">Masuk</p>
            <h2 className="mt-2 font-display text-2xl font-semibold tracking-tight">Akses akun Anda</h2>
          </div>

          {(serverDown || mismatch) && (
            <div className="space-y-3 rounded-2xl border border-amber-500/40 bg-amber-500/5 p-4" data-testid="login-net-warning">
              <div className="flex items-start gap-2">
                <WifiOff className="mt-0.5 h-4 w-4 shrink-0 text-amber-400" />
                <div className="space-y-1 text-xs leading-relaxed">
                  <p className="font-semibold text-amber-300">
                    {mismatch ? "Anda membuka alamat yang salah/lama" : "Server aplikasi tidak terjangkau"}
                  </p>
                  <p className="text-muted-foreground">
                    {mismatch
                      ? "Alamat yang Anda buka berbeda dengan alamat server aplikasi. Buka alamat resmi di bawah ini, lalu pasang ulang aplikasi di HP bila perlu."
                      : "Login gagal karena aplikasi tidak bisa menghubungi server (Network Error). Periksa koneksi internet, lalu tekan Cek ulang. Bila Anda memakai pintasan/aplikasi lama di HP, buka alamat resmi di bawah ini."}
                  </p>
                  <p className="break-all font-mono text-[11px] text-cyan-400">{appUrl}</p>
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button
                  type="button" size="sm" variant="outline" className="rounded-full text-[11px]"
                  onClick={check} disabled={checking} data-testid="login-net-recheck"
                >
                  {checking ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><RefreshCw className="mr-1 h-3.5 w-3.5" /> Cek ulang</>}
                </Button>
                <Button
                  asChild size="sm" className="rounded-full bg-gradient-to-r from-cyan-500 to-violet-600 text-[11px] text-white"
                >
                  <a href={`${appUrl}/login`} data-testid="login-open-correct-url">Buka alamat resmi</a>
                </Button>
              </div>
            </div>
          )}

          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="username" className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Username</Label>
              <div className="relative">
                <User className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  id="username" data-testid="login-username" value={username} autoComplete="username"
                  onChange={(e) => setUsername(e.target.value)} placeholder="username"
                  className="pl-9 h-11 rounded-xl bg-secondary/60"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Password</Label>
              <div className="relative">
                <Lock className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  id="password" type="password" data-testid="login-password" value={password} autoComplete="current-password"
                  onChange={(e) => setPassword(e.target.value)} placeholder="••••••••"
                  className="pl-9 h-11 rounded-xl bg-secondary/60"
                />
              </div>
            </div>
          </div>

          <Button
            type="submit" disabled={busy} data-testid="login-submit"
            className="h-11 w-full rounded-full bg-gradient-to-r from-cyan-500 to-violet-600 text-white hover:opacity-90 transition-opacity duration-200"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Masuk"}
          </Button>

          <Link
            to="/instal" data-testid="login-install-link"
            className="flex items-center justify-center gap-1.5 text-xs text-muted-foreground transition-colors duration-200 hover:text-violet-400"
          >
            <Smartphone className="h-3.5 w-3.5" /> Pasang aplikasi di HP (panduan)
          </Link>

          <div className="space-y-3 rounded-2xl border border-border bg-card/60 p-4 text-center" data-testid="login-register-box">
            <p className="text-xs text-muted-foreground">
              Punya usaha RT-RW Net dan ingin memakai dasbor ALPHA BILL? Daftar sebagai admin, pilih paket sewa bulanan atau tahunan.
            </p>
            <Button
              asChild type="button" variant="outline"
              className="h-10 w-full rounded-full border-violet-500/50 text-violet-300 hover:bg-violet-500/10"
            >
              <Link to="/daftar" data-testid="login-register-link">
                <UserPlus className="mr-1.5 h-4 w-4" /> Daftar Akun Admin Baru
              </Link>
            </Button>
          </div>

          <p className="text-center font-mono text-[10px] text-muted-foreground" data-testid="login-server-status">
            server: {net === null ? "memeriksa…" : net.ok ? `terhubung (${net.ms} ms)` : `tidak terhubung${net.status ? ` (HTTP ${net.status})` : ""}`}
          </p>
        </form>
      </div>
    </div>
  );
}
